/**
 * Page combinée Détail/Modification d'un Chapitre
 *
 * - Mode lecture par défaut
 * - Bouton Modifier → mode édition inline
 * - Création nouveau chapitre si id === 'new' (bookId requis en query param)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
} from '@mui/material';
import { ArrowBack, Edit, Save, Cancel } from '@mui/icons-material';
import { BookChapter, SchoolBook } from '../../types';
import SchoolBooksApiService from '../../services/schoolBooksApi';
import { toUserMessage } from '../../lib/http';
import { useNotify } from '../../contexts/NotificationContext';
import { logger } from '../../lib/logger';

const BookChapterView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNewMode = id === 'new';

  // Pour création, bookId vient des query params
  const bookIdFromParams = searchParams.get('bookId');

  const { environment: env } = useEnvironment();
  const [chapter, setChapter] = useState<BookChapter | null>(null);
  const [book, setBook] = useState<SchoolBook | null>(null);
  const [loading, setLoading] = useState(!isNewMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(isNewMode);
  const { notify } = useNotify('content');

  // Données du formulaire
  const [formData, setFormData] = useState<Partial<BookChapter>>({
    bookId: bookIdFromParams || '',
    chapterNumber: 1,
    title: '',
    description: '',
    nomCourt: '',
    estimatedDuration: undefined,
    lessonsCount: 0
  });

  // Charger le chapitre existant
  const loadChapter = useCallback(async () => {
    if (isNewMode) return;

    try {
      setLoading(true);
      const data = await SchoolBooksApiService.getBookChapterById(env, id!);
      setChapter(data);
      setFormData(data);

      // Charger le livre parent pour afficher son titre
      if (data.bookId) {
        const bookData = await SchoolBooksApiService.getSchoolBookById(env, data.bookId);
        setBook(bookData);
      }
    } catch (err: unknown) {
      setError('Erreur lors du chargement du chapitre');
    } finally {
      setLoading(false);
    }
  }, [id, env, isNewMode]);

  // Charger le livre parent (pour création)
  const loadParentBook = useCallback(async () => {
    if (!isNewMode || !bookIdFromParams) return;

    try {
      const bookData = await SchoolBooksApiService.getSchoolBookById(env, bookIdFromParams);
      setBook(bookData);
    } catch (err) {
      logger.error('Erreur lors du chargement du livre parent:', err);
    }
  }, [bookIdFromParams, env, isNewMode]);

  useEffect(() => {
    if (isNewMode) {
      loadParentBook();
    } else if (id) {
      loadChapter();
    }
  }, [loadChapter, loadParentBook, id, isNewMode]);

  // Handlers
  const handleChange = <Field extends keyof BookChapter>(field: Field, value: BookChapter[Field]) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    if (isNewMode) {
      navigate('/book-chapters');
    } else {
      if (chapter) {
        setFormData(chapter);
      }
      setIsEditMode(false);
      setError('');
    }
  };

  const validateForm = (): boolean => {
    if (!formData.title || formData.title.trim().length < 3) {
      setError('Le titre doit contenir au moins 3 caractères');
      return false;
    }
    if (!formData.description || formData.description.trim().length < 10) {
      setError('La description doit contenir au moins 10 caractères');
      return false;
    }
    if (!formData.nomCourt || formData.nomCourt.trim().length < 2) {
      setError('Le nom court doit contenir au moins 2 caractères');
      return false;
    }
    if (!formData.chapterNumber || formData.chapterNumber < 1) {
      setError('Le numéro de chapitre doit être supérieur ou égal à 1');
      return false;
    }
    if (!formData.bookId) {
      setError('Le livre parent est requis');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    setError('');

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      if (isNewMode) {
        const response = await SchoolBooksApiService.createBookChapter(env, formData as Omit<BookChapter, 'id'>);
        if (response.data?.id) {
          notify('Chapitre créé avec succès', 'success');
          navigate(`/book-chapters/${response.data.id}`, { replace: true });
        } else {
          throw new Error(response.error || 'Erreur lors de la création');
        }
      } else {
        await SchoolBooksApiService.updateBookChapter(env, id!, formData);
        setChapter({ ...chapter, ...formData } as BookChapter);
        setIsEditMode(false);
        notify('Chapitre mis à jour avec succès', 'success');
      }
    } catch (err: unknown) {
      setError(toUserMessage(err, 'Erreur lors de la sauvegarde'));
      notify('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Vérifier bookId pour création
  if (isNewMode && !bookIdFromParams) {
    return (
      <Box p={3}>
        <Alert severity="error">Le paramètre bookId est requis pour créer un chapitre</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/book-chapters')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  // Error state
  if (!isNewMode && !chapter && !loading) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Chapitre non trouvé'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/book-chapters')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/book-chapters')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flex: 1 }}>
          {isNewMode
            ? 'Nouveau Chapitre'
            : (isEditMode
              ? 'Modifier le chapitre'
              : `Chapitre ${formData.chapterNumber}: ${formData.title}`
            )
          }
        </Typography>

        {!isEditMode && !isNewMode && (
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={handleEdit}
          >
            Modifier
          </Button>
        )}

        {isEditMode && (
          <Box>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              sx={{ mr: 1 }}
              disabled={saving}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Livre parent */}
      {book && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Livre : <strong>{book.title}</strong>
        </Alert>
      )}

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {isEditMode ? 'Informations du chapitre' : 'Détails'}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {isEditMode ? (
          /* Mode Édition */
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Numéro du chapitre"
                type="number"
                value={formData.chapterNumber || 1}
                onChange={(e) => handleChange('chapterNumber', parseInt(e.target.value) || 1)}
                required
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Durée estimée (minutes)"
                type="number"
                value={formData.estimatedDuration || ''}
                onChange={(e) => handleChange('estimatedDuration', e.target.value ? parseInt(e.target.value) : undefined)}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titre"
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                required
                helperText="Minimum 3 caractères"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom court (titre abrégé)"
                value={formData.nomCourt || ''}
                onChange={(e) => handleChange('nomCourt', e.target.value)}
                required
                helperText="Minimum 2 caractères (ex: Chap 1, Th. Pythagore)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                required
                multiline
                rows={4}
                helperText="Minimum 10 caractères"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre de leçons"
                value={formData.lessonsCount || 0}
                type="number"
                InputProps={{ readOnly: true }}
                helperText="Calculé automatiquement"
              />
            </Grid>
          </Grid>
        ) : (
          /* Mode Lecture */
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Numéro du chapitre
              </Typography>
              <Typography variant="body1">{formData.chapterNumber || '-'}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Nom court
              </Typography>
              <Typography variant="body1">{formData.nomCourt || '-'}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Description
              </Typography>
              <Typography variant="body1" paragraph>
                {formData.description || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Durée estimée
              </Typography>
              <Typography variant="body1">
                {formData.estimatedDuration ? `${formData.estimatedDuration} minutes` : '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Nombre de leçons
              </Typography>
              <Typography variant="body1">{formData.lessonsCount || 0}</Typography>
            </Grid>
          </Grid>
        )}
      </Paper>

    </Box>
  );
};

export default BookChapterView;
